/**
 * Dictation helpers: the browser's Web Speech API (`SpeechRecognition`, prefixed in Chrome and
 * Safari) typed locally, since TypeScript's lib.dom has no constructor for it, plus the pure text
 * and message rules the composer uses.
 */

export interface SpeechAlternativeLike {
  transcript: string;
}

export interface SpeechResultLike {
  isFinal: boolean;
  0: SpeechAlternativeLike;
}

export interface SpeechResultEventLike {
  resultIndex: number;
  results: { length: number; [index: number]: SpeechResultLike };
}

export interface SpeechErrorEventLike {
  error: string;
}

export interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  /** Chrome's on-device recognition; absent elsewhere. */
  processLocally?: boolean;
  onresult: ((e: SpeechResultEventLike) => void) | null;
  onerror: ((e: SpeechErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export type OnDeviceStatus = 'available' | 'downloadable' | 'downloading' | 'unavailable';

export interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
  /** Recent Chrome only. */
  available?(options: { langs: string[]; processLocally?: boolean }): Promise<OnDeviceStatus>;
  install?(options: { langs: string[]; processLocally?: boolean }): Promise<boolean>;
}

type SpeechWindow = {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
};

export const speechRecognitionCtor = (
  win: SpeechWindow = (window as unknown) as SpeechWindow,
): SpeechRecognitionCtor | null => win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;

/** Adds dictated words to the typed text with one space between them. */
export function appendDictation(current: string, addition: string): string {
  const text = addition.trim();
  if (!text) return current;
  if (current === '' || /\s$/.test(current)) return current + text;
  return `${current} ${text}`;
}

export const MIC_BLOCKED_MESSAGE = "Microphone blocked. Allow it in your browser's site settings.";

/** The short message for a recognition error code, or null when nothing should be shown. */
export function dictationErrorMessage(code: string): string | null {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return MIC_BLOCKED_MESSAGE;
    case 'audio-capture':
      return 'No microphone found.';
    case 'network':
      return "Dictation isn't available right now.";
    case 'no-speech':
      return "Didn't catch that. Try again.";
    case 'aborted':
      return null;
    default:
      return 'Dictation stopped.';
  }
}
